import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { Loader2 } from 'lucide-react'

const fieldClass =
  'w-full rounded-2xl border border-hairline bg-surface px-3.5 py-2 text-sm text-ink outline-none transition placeholder:text-faint focus:border-accent focus:bg-surface focus:ring-4 focus:ring-accent disabled:opacity-60'

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-ink">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  )
}

export function TextInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${fieldClass} ${props.className ?? ''}`} />
}

export function TextArea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${fieldClass} font-mono ${props.className ?? ''}`} />
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${fieldClass} ${props.className ?? ''}`} />
}

export function PrimaryButton({
  children,
  busy,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; children: ReactNode }) {
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-accent-deep px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:brightness-105 disabled:opacity-60 ${props.className ?? ''}`}
    >
      {busy && <Loader2 size={15} className="animate-spin" />}
      {children}
    </button>
  )
}

export function GhostButton({
  children,
  busy,
  tone = 'neutral',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  busy?: boolean
  tone?: 'neutral' | 'danger'
  children: ReactNode
}) {
  const toneClass =
    tone === 'danger'
      ? 'text-danger hover:bg-danger-wash'
      : 'text-muted hover:bg-surface hover:text-ink'
  return (
    <button
      {...props}
      disabled={props.disabled || busy}
      className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-surface px-3.5 py-2 text-sm font-semibold shadow-sm transition disabled:opacity-60 ${toneClass} ${props.className ?? ''}`}
    >
      {busy && <Loader2 size={15} className="animate-spin" />}
      {children}
    </button>
  )
}

/** 400s carry `detail` strings written for humans, so they are shown verbatim. */
export function FormError({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p className="rounded-2xl bg-danger-wash px-3.5 py-2 text-sm font-medium text-danger">
      {message}
    </p>
  )
}

export function FormNotice({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <p className="rounded-2xl bg-info-wash px-3.5 py-2 text-sm font-medium text-info">
      {message}
    </p>
  )
}
